import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Loading } from 'components/Loading'
import showMessage from 'components/showMessage'
import { nonScheduleModality } from 'contexts/dockCourses'
import { scheduleViewUrl } from './scheduleView'
import { DOCK_EXAM_END, DOCK_EXAM_START, ES_LOWER_DAILY_LIMIT, MAX_WAIT_MINUTES, GYNECOLOGY_END, GYNECOLOGY_START, getDockModalityTime, getHolidayName } from 'lib/dockSchedule'
import { useFirebaseApp } from 'contexts/FirebaseApp'
import { CANCEL_STATUS, DOCK_TYPES } from 'contexts/enviroments'
import { CheckClass, InputClass, SmallButtonClass } from 'contexts/style'
import { JSTDate, JSTTime, JSTWeekday } from 'contexts/dateUtils'
import {
  Candidate, CourseContent, CourseProfile, DiagnoseAttempt, DockInfo, Gender, Person, PersonSchedule,
  analyzeCoursePairs, analyzeCourses, contentKey, diagnoseDay, findCandidatesAsync, getCourseContents, getCourseMaxStayMinutes, getCourseOptions, getDockWeekdays, getReceptionTimes, nextDockDay,
} from 'contexts/dockCandidate'

// コース分析・候補日の探索の対象期間（過去3ヶ月〜3ヶ月後）
const ANALYZE_MONTHS_BEFORE = 3
const ANALYZE_MONTHS_AFTER = 3
// 候補日を探す期間（検索開始日から何か月か）の選択肢と初期値、検索開始日の初期値（今日から何日後か）
const SEARCH_MONTH_OPTIONS = [1, 2, 3]
const DEFAULT_SEARCH_MONTHS = 3
const DEFAULT_SEARCH_START_DAYS = 7
const MAX_PEOPLE = 4
const CANDIDATE_LIMIT = 5

// 操作の行のボタン（高さを揃える）。主役の「候補を出力」だけ塗りつぶし
const PRIMARY_BUTTON = 'inline-flex items-center h-9 px-5 rounded-md text-sm font-medium text-white bg-theme-600 hover:bg-theme-700 disabled:opacity-50'
const SMALL_OUTLINE_BUTTON = 'inline-flex items-center h-7 px-2 rounded-md text-xs border border-theme-600 text-theme-700 bg-white hover:bg-theme-50'
const OUTLINE_BUTTON = 'inline-flex items-center h-9 px-3 rounded-md text-sm border border-theme-600 text-theme-700 bg-white hover:bg-theme-50'

const GENDER_LABELS: Record<Gender, string> = { M: '男性', F: '女性' }
const DAY_LABELS = ['1日目', '2日目']
// 1人目との受付時刻の差の上限(分)。1日目・2日目の両方にかける
const GAP_OPTIONS: { label: string, value?: number }[] = [
  { label: '指定なし' },
  { label: '同時刻', value: 0 },
  { label: '30分以内', value: 30 },
  { label: '60分以内', value: 60 },
]

// 1日分のコース。courseName が空なら、その日は受診しない
type DayInput = {
  courseName: string
  selected?: string[]  // 選択した検査(contentKey)。未指定ならコースで最も多い構成
}

type PersonInput = {
  gender: Gender
  days: [DayInput, DayInput]
}

const newPerson = (gender: Gender, days: [DayInput, DayInput]): PersonInput => ({ gender, days: [{ courseName: days[0].courseName }, { courseName: days[1].courseName }] })

const BOOKING_PROJECTION = { modalityID: 1, body1: 1, body2: 1, contrast: 1, start: 1, end: 1, bookingType: 1, bookingStatus: 1, courseName: 1, dockID: 1, patientID: 1, ivCheckin: 1, checkin: 1 }

const addDays = (date: Date, days: number) => {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  result.setDate(result.getDate() + days)
  return result
}
// input[type=date] の値（YYYY-MM-DD）
const toDateInput = (date: Date) => `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(-2)}`
const fromDateInput = (value: string) => new Date(`${value}T00:00:00`)

const addMonths = (date: Date, months: number) => {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

const formatScheduledExams = (exams: { modalityID: string, body1: string, body2: string, contrast: string, start: Date, end: Date, noSchedule: boolean, ivCheckin?: string }[]) =>
  exams.filter(exam => !exam.noSchedule).map(exam =>
    `${JSTTime(exam.start)}〜${JSTTime(exam.end)} ${formatExam(exam.modalityID, exam.body1, exam.body2, exam.contrast)}${exam.ivCheckin ? `（IV ${exam.ivCheckin}）` : ''}`
  ).join(' / ')

// 時間（分）を「3時間30分」の形に
const formatHours = (minutes: number) => `${Math.floor(minutes / 60)}時間${minutes % 60 ? `${minutes % 60}分` : ''}`

// 受付から全検査終了まで
const formatStay = (minutes: number) => `所要${Math.floor(minutes / 60)}時間${minutes % 60 ? `${minutes % 60}分` : ''}`

const formatExam = (modality: string, body1: string, body2: string, contrast: string) =>
  [modality, body1, body2, contrast].filter(Boolean).join(' ')

const formatDate = (date: Date) => `${JSTDate(date)}（${JSTWeekday(date)}）`

const toGender = (value?: string): Gender | undefined => {
  if (value === 'M' || value === '男' || value === '男性') return 'M'
  if (value === 'F' || value === '女' || value === '女性') return 'F'
  return undefined
}

const findCourseIn = (courses: CourseProfile[], name: string) => courses.find(v => v.courseName === name)

const Candidates = () => {
  const { functions } = useFirebaseApp()
  const [loading, setLoading] = useState(false)
  const [baseResources, setBaseResources] = useState<KV[]>([])
  const [dockBookings, setDockBookings] = useState<KV[]>([])
  const [dockInfos, setDockInfos] = useState<Record<string, DockInfo>>({})
  const [twoDays, setTwoDays] = useState(false)
  const [sameCourse, setSameCourse] = useState(true)
  // 「制限時間を外す」: コースごとの所要時間の上限で除外しない（現場の状況で長くなってもよい場合）
  const [noStayLimit, setNoStayLimit] = useState(false)
  const [people, setPeople] = useState<PersonInput[]>([newPerson('M', [{ courseName: '' }, { courseName: '' }])])
  // 検査のチェックボックスを開いているマス（`${人}-${日}`）
  const [openOptions, setOpenOptions] = useState<string | null>(null)
  const [maxGap, setMaxGap] = useState<number | undefined>(undefined)
  // 候補を探し始める日と、そこから何か月探すか
  const [searchStart, setSearchStart] = useState(() => toDateInput(addDays(new Date(), DEFAULT_SEARCH_START_DAYS)))
  const [searchMonths, setSearchMonths] = useState(DEFAULT_SEARCH_MONTHS)
  const [candidates, setCandidates] = useState<Candidate[] | null>(null)
  // 候補の探索中の進み具合（探索した日数 / 全日数）
  const [progress, setProgress] = useState<{ done: number, total: number } | null>(null)
  const cancelRef = useRef(false)
  const [diagnoseDate, setDiagnoseDate] = useState('')
  const [diagnosis, setDiagnosis] = useState<{ days: { date: Date, rows: number[], attempts: DiagnoseAttempt[][] }[], missingSecond: boolean } | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setCandidates(null)
    try {
      // /dock 画面と同じ所要時間マスタ
      const response = await fetch(process.env.PUBLIC_URL + '/contexts/modality_dock.json', { cache: 'reload' })
      setBaseResources(await response.json())

      const now = new Date()
      // コース分析用: ドック予約
      const docksData = (await functions(['mongo/client', {
        collection: 'bookings',
        find: {
          filter: {
            start: { $gte: addMonths(now, -ANALYZE_MONTHS_BEFORE), $lt: addMonths(now, ANALYZE_MONTHS_AFTER) },
            bookingStatus: { $ne: CANCEL_STATUS },
            bookingType: { $in: DOCK_TYPES },
          },
          options: { projection: BOOKING_PROJECTION },
        },
      }])) as KV[]
      // 性別・受付時刻はドック予約(docks)から取得
      const dockIDs = Array.from(new Set(docksData.filter(v => v.dockID).map(v => String(v.dockID))))
      const docks = dockIDs.length ? (await functions(['mongo/client', {
        collection: 'docks',
        find: {
          filter: { book_id: { $in: dockIDs.map(Number) } },
          options: { projection: { book_id: 1, gender: 1, reception_time: 1 } },
        },
      }])) as KV[] : []
      setDockBookings(docksData)
      setDockInfos(Object.fromEntries(docks.map(v => [String(v.book_id), { gender: toGender(v.gender), receptionTime: v.reception_time }])))
    } catch (e) {
      await showMessage(`予約データの取得に失敗しました。\r\n${e instanceof Error ? e.message : e}`, { error: true })
    } finally {
      setLoading(false)
    }
  }, [functions])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  /**
   * 空き状況用に、from〜until（until の日を含む）の全予約を読み込む。「候補を出力」「診断」のたびに呼び、常に最新の予約で判定する
   * @returns 読み込みに失敗した場合は undefined
   */
  const fetchBookings = async (from: Date, until: Date) => {
    setLoading(true)
    try {
      return (await functions(['mongo/client', {
        collection: 'bookings',
        find: {
          filter: {
            start: { $gte: addDays(from, 0), $lt: addDays(until, 1) },
            bookingStatus: { $ne: CANCEL_STATUS },
          },
          options: { projection: BOOKING_PROJECTION },
        },
      }])) as KV[]
    } catch (e) {
      await showMessage(`予約データの取得に失敗しました。\r\n${e instanceof Error ? e.message : e}`, { error: true })
      return undefined
    } finally {
      setLoading(false)
    }
  }

  const courses = useMemo(() => analyzeCourses(dockBookings, dockInfos), [dockBookings, dockInfos])
  const coursePairs = useMemo(() => analyzeCoursePairs(dockBookings), [dockBookings])
  const weekdays = useMemo(() => getDockWeekdays(dockBookings), [dockBookings])
  const findCourse = (name: string): CourseProfile | undefined => courses.find(v => v.courseName === name)
  const dayCount = twoDays ? 2 : 1

  // 1日目のコースが未選択なら、最も件数の多いコースを設定
  useEffect(() => {
    if (courses.length && people.some(v => !findCourseIn(courses, v.days[0].courseName))) {
      setPeople(prev => prev.map(v => (findCourseIn(courses, v.days[0].courseName) ? v : { ...v, days: [{ courseName: courses[0].courseName }, v.days[1]] })))
    }
  }, [courses, people])

  const modalityTime = (v: CourseContent) => {
    // XP・問診・子宮細胞診・HPV などは再計算で時間を管理しない（予約確定時に受付からの固定の枠で登録）
    if (nonScheduleModality.includes(v.modality)) return '時間管理なし'
    try {
      return `${getDockModalityTime(baseResources, v.modality, v.modality_body1, v.modality_body2, v.modality_contrast)}分`
    } catch (e) {
      return '所要時間不明'
    }
  }

  const resetResults = () => {
    setCandidates(null)
    setDiagnosis(null)
  }
  const updatePerson = (index: number, value: Partial<PersonInput>) => {
    setPeople(prev => prev.map((v, i) => (i === index ? { ...v, ...value } : v)))
    resetResults()
  }
  // コースを変えると検査の選択は初期選択に戻る。「全員同じコース」なら1人目のコースを全員に反映
  const updateCourse = (index: number, day: number, courseName: string) => {
    setPeople(prev => prev.map((v, i) => {
      if (i !== index && !(sameCourse && index === 0)) return v
      const days = [...v.days] as [DayInput, DayInput]
      days[day] = { courseName }
      return { ...v, days }
    }))
    resetResults()
  }
  const applyPair = (value: string) => {
    const pair = coursePairs[Number(value)]
    if (!pair) return
    setTwoDays(true)
    setPeople(prev => prev.map(v => ({ ...v, days: [{ courseName: pair.first }, { courseName: pair.second }] })))
    resetResults()
  }
  const addPerson = () => {
    // 2人目は夫婦を想定して女性、コースは1人目と同じ
    setPeople(prev => [...prev, newPerson(prev.length === 1 ? 'F' : 'M', prev[0].days)])
    resetResults()
  }
  const removePerson = (index: number) => {
    setPeople(prev => prev.filter((_, i) => i !== index))
    setOpenOptions(null)
    resetResults()
  }

  // 検索に使う検査構成
  const getContents = (gender: Gender, day: DayInput): CourseContent[] => {
    const course = findCourse(day.courseName)
    if (!course) return []
    if (!day.selected) return getCourseContents(course, gender)
    return getCourseOptions(course, gender).map(v => v.content).filter(v => day.selected!.includes(contentKey(v)))
  }
  const toggleOption = (index: number, day: number, key: string) => {
    const person = people[index]
    const selected = getContents(person.gender, person.days[day]).map(contentKey)
    const days = [...person.days] as [DayInput, DayInput]
    days[day] = { ...days[day], selected: selected.includes(key) ? selected.filter(v => v !== key) : [...selected, key] }
    updatePerson(index, { days })
  }

  // 検索・診断の入力。日ごとに、その日に受診する人（rows: 何人目か）と検査構成
  const getSearchInput = async () => {
    const days = Array.from({ length: dayCount }, (_, day) => {
      const rows = people.map((_, i) => i).filter(i => people[i].days[day].courseName)
      const targets: Person[] = rows.map(i => {
        const { gender, days } = people[i]
        const course = findCourse(days[day].courseName)
        return {
          courseName: days[day].courseName, gender, contents: getContents(gender, days[day]), receptionTimes: course ? getReceptionTimes(course) : [],
          // 受付から全検査終了までの上限（コースごと。「制限時間を外す」なら上限なし）
          maxStayMinutes: noStayLimit ? undefined : getCourseMaxStayMinutes(days[day].courseName),
        }
      })
      return { rows, targets }
    })
    if (!days[0].targets.length) {
      await showMessage('1日目のコースを選択してください。', { error: true })
      return undefined
    }
    if (days.some(v => v.targets.some(t => !t.contents.length))) {
      await showMessage('検査が選択されていないコースがあります。', { error: true })
      return undefined
    }
    return days
  }

  const handleDiagnose = async () => {
    if (!diagnoseDate) return
    const input = await getSearchInput()
    if (!input) return
    // 診断する日と、2日連続の場合の2日目（翌営業日は2週間以内）の予約
    const first = fromDateInput(diagnoseDate)
    const futureBookings = await fetchBookings(first, addDays(first, 14))
    if (!futureBookings) return
    try {
      const second = input[1]?.targets.length ? nextDockDay(first, weekdays) : undefined
      setDiagnosis({
        days: [
          { date: first, rows: input[0].rows, attempts: diagnoseDay(first, input[0].targets, futureBookings, baseResources) },
          ...(second ? [{ date: second, rows: input[1].rows, attempts: diagnoseDay(second, input[1].targets, futureBookings, baseResources) }] : []),
        ],
        missingSecond: !!input[1]?.targets.length && !second,
      })
    } catch (e) {
      // 計算中のエラーで何も表示されないまま終わらないように、内容を表示する
      await showMessage(`診断中にエラーが発生しました。\r\n${e instanceof Error ? e.message : e}`, { error: true })
    }
  }

  // 検索したときの「何人目か」の対応（候補の表示に使う）
  const [searchRows, setSearchRows] = useState<number[][]>([])
  const handleSearch = async () => {
    const input = await getSearchInput()
    if (!input) return
    setCandidates(null)
    // 検索開始日から searchMonths か月（明日より前は選べない）。10/28 から1か月なら 11/27 まで
    const from = new Date(Math.max(fromDateInput(searchStart).getTime(), addDays(new Date(), 1).getTime()))
    const until = addDays(addMonths(from, searchMonths), -1)
    const futureBookings = await fetchBookings(from, until)
    if (!futureBookings) return
    cancelRef.current = false
    setSearchRows(input.map(v => v.rows))
    setProgress({ done: 0, total: 0 })
    try {
      setCandidates(await findCandidatesAsync({
        from,
        until,
        people: input[0].targets,
        secondDay: input[1]?.targets,
        bookings: futureBookings,
        baseResources,
        weekdays,
        maxGap,
        limit: CANDIDATE_LIMIT,
        onProgress: (done, total) => setProgress({ done, total }),
        isCancelled: () => cancelRef.current,
      }))
    } catch (e) {
      await showMessage(`候補の検索中にエラーが発生しました。\r\n${e instanceof Error ? e.message : e}`, { error: true })
    } finally {
      setProgress(null)
    }
  }

  // 候補の1日分を、その日の予約表（確認用のページ）に重ねて新しいタブで開く
  const openSchedule = (title: string, date: Date, persons: Record<string, PersonSchedule>) => {
    window.open(scheduleViewUrl({
      title,
      date: toDateInput(date),
      persons: Object.entries(persons).map(([row, person]) => ({
        label: `${Number(row) + 1}人目（${GENDER_LABELS[person.gender]}）`,
        reception: person.reception.toISOString(),
        exams: person.exams.filter(exam => !exam.noSchedule).map(exam => ({
          modalityID: exam.modalityID, body2: exam.body2, contrast: exam.contrast,
          start: exam.start.toISOString(), end: exam.end.toISOString(), ivCheckin: exam.ivCheckin,
        })),
      })),
    }), '_blank')
  }

  // 候補の1日分を、何人目かごとに引けるようにする
  const byRow = (persons: PersonSchedule[], rows: number[]) => Object.fromEntries(persons.map(p => [rows[p.index], p]))

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="text-lg font-bold">ドック予約候補日検索</h2>
        <span className="text-sm text-gray-500">
          過去{ANALYZE_MONTHS_BEFORE}ヶ月〜{ANALYZE_MONTHS_AFTER}ヶ月後のドック予約から {courses.length}コースを分析
        </span>
      </div>
      {loading && <Loading full />}

      <div className="bg-white rounded-md shadow-sm p-3 text-sm space-y-3">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <div>日程</div>
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
              {['1日で完了', '2日連続'].map((label, k) => (
                <button key={label} type="button" className={`px-3 py-2 ${twoDays === (k === 1) ? 'bg-theme-600 text-white' : 'bg-white'}`}
                  onClick={() => { setTwoDays(k === 1); resetResults() }}>{label}</button>
              ))}
            </div>
          </div>
          {coursePairs.length > 0 && (
            <label>
              セット（過去に続けて受けたコース）
              <select className={InputClass} value="" onChange={e => applyPair(e.target.value)}>
                <option value="">選択してください</option>
                {coursePairs.slice(0, 10).map((v, k) => <option key={k} value={k}>{v.first} → {v.second}（{v.count}件）</option>)}
              </select>
            </label>
          )}
        </div>

        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-600">
              <th className="w-28 font-normal"></th>
              {DAY_LABELS.slice(0, dayCount).map((label, day) => (
                <th key={label} className="font-normal pb-1">{label}{day === 1 && '（翌営業日）'}</th>
              ))}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {people.map((person, i) => (
              <tr key={i} className="align-top border-t border-gray-100">
                <td className="py-2 pr-2">
                  <div className="font-medium">{i + 1}人目</div>
                  <select className={InputClass} value={person.gender} onChange={e => updatePerson(i, { gender: e.target.value as Gender, days: [{ courseName: person.days[0].courseName }, { courseName: person.days[1].courseName }] })}>
                    {(Object.keys(GENDER_LABELS) as Gender[]).map(g => <option key={g} value={g}>{GENDER_LABELS[g]}</option>)}
                  </select>
                </td>
                {person.days.slice(0, dayCount).map((day, d) => {
                  const course = findCourse(day.courseName)
                  const pattern = course && (course.patterns[person.gender] ?? course.patterns.ALL)
                  const selectedKeys = getContents(person.gender, day).map(contentKey)
                  const open = openOptions === `${i}-${d}`
                  return (
                    <td key={d} className="py-2 pr-3">
                      <select className={InputClass + ' w-full'} value={day.courseName} disabled={sameCourse && i > 0}
                        onChange={e => updateCourse(i, d, e.target.value)}>
                        {d === 1 && <option value="">（受診しない）</option>}
                        {courses.map(v => <option key={v.courseName} value={v.courseName}>{v.courseName}（{v.count}件）</option>)}
                      </select>
                      {day.courseName && (
                        <div className="text-gray-500 mt-1">
                          所要時間の上限 {noStayLimit ? 'なし（制限時間を外す）' : getCourseMaxStayMinutes(day.courseName) ? formatHours(getCourseMaxStayMinutes(day.courseName)!) : 'なし'}
                        </div>
                      )}
                      {course && (pattern ? (
                        <button type="button" className="underline text-theme-700 mt-1" onClick={() => setOpenOptions(open ? null : `${i}-${d}`)}>
                          検査{selectedKeys.length}件・オプション{open ? 'を閉じる' : '変更'}
                        </button>
                      ) : <div className="text-red-600 mt-1">検査構成の実績なし</div>)}
                      {open && course && pattern && (
                        <div className="mt-2 space-y-1">
                          {getCourseOptions(course, person.gender).map(option => {
                            const key = contentKey(option.content)
                            const v = option.content
                            return (
                              <label key={key} className="flex items-center gap-1">
                                <input type="checkbox" className={CheckClass} checked={selectedKeys.includes(key)} onChange={() => toggleOption(i, d, key)} />
                                {formatExam(v.modality, v.modality_body1, v.modality_body2, v.modality_contrast)}({modalityTime(v)})
                                <span className="text-gray-400">{option.count}件</span>
                              </label>
                            )
                          })}
                          <div className="text-gray-500">
                            初期選択はコースで最も多い構成（{pattern.total}件中{pattern.count}件）
                            {day.selected && <button type="button" className="ml-2 underline" onClick={() => {
                              const days = [...person.days] as [DayInput, DayInput]
                              days[d] = { courseName: day.courseName }
                              updatePerson(i, { days })
                            }}>初期選択に戻す</button>}
                          </div>
                        </div>
                      )}
                    </td>
                  )
                })}
                <td className="py-2">
                  {i > 0 && <button type="button" className={SmallButtonClass + ' py-1 px-2'} onClick={() => removePerson(i)}>削除</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center gap-4 flex-wrap border-t border-gray-100 pt-3">
          {people.length < MAX_PEOPLE && (
            <button type="button" className={OUTLINE_BUTTON} onClick={addPerson}>＋ 家族を追加（最大{MAX_PEOPLE}名）</button>
          )}
          <label className="inline-flex items-center gap-1">
            <input type="checkbox" className={CheckClass} checked={noStayLimit} onChange={e => { setNoStayLimit(e.target.checked); resetResults() }} />
            制限時間を外す
          </label>
          {people.length > 1 && <>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" className={CheckClass} checked={sameCourse} onChange={e => {
                setSameCourse(e.target.checked)
                if (e.target.checked) setPeople(prev => prev.map(v => ({ ...v, days: [{ courseName: prev[0].days[0].courseName }, { courseName: prev[0].days[1].courseName }] })))
                resetResults()
              }} />
              全員同じコース
            </label>
            <label className="inline-flex items-center gap-2">
              受付時刻の差
              <select className={InputClass + ' h-9 py-1'} value={maxGap ?? ''} onChange={e => { setMaxGap(e.target.value === '' ? undefined : Number(e.target.value)); resetResults() }}>
                {GAP_OPTIONS.map(v => <option key={v.label} value={v.value ?? ''}>{v.label}</option>)}
              </select>
            </label>
          </>}
          <div className="ml-auto flex items-center gap-3">
            <label className="inline-flex items-center gap-2">
              検索開始日
              <input type="date" className={InputClass + ' h-9 py-1'} value={searchStart} min={toDateInput(addDays(new Date(), 1))}
                onChange={e => { if (e.target.value) setSearchStart(e.target.value); resetResults() }} />
            </label>
            <label className="inline-flex items-center gap-2">
              から
              <select className={InputClass + ' h-9 py-1'} value={searchMonths} onChange={e => { setSearchMonths(Number(e.target.value)); resetResults() }}>
                {SEARCH_MONTH_OPTIONS.map(n => <option key={n} value={n}>{n}か月</option>)}
              </select>
            </label>
            {progress && <>
              <span className="text-gray-600">探索中… {progress.done}/{progress.total}日</span>
              <button type="button" className={OUTLINE_BUTTON} onClick={() => { cancelRef.current = true }}>中止</button>
            </>}
            <button type="button" className={PRIMARY_BUTTON} onClick={handleSearch} disabled={!courses.length || loading || !!progress}>
              候補を出力
            </button>
          </div>
        </div>
      </div>

      {candidates && (candidates.length === 0 ? (
        <div className="text-red-600 text-sm">{formatDate(fromDateInput(searchStart))}から{searchMonths}か月以内に、全員分の枠が取れる日はありませんでした。</div>
      ) : (
        <div className="space-y-3">
          {candidates.map((candidate, index) => {
            const days = [{ date: candidate.date, persons: byRow(candidate.persons, searchRows[0] ?? []) },
              ...(candidate.second ? [{ date: candidate.second.date, persons: byRow(candidate.second.persons, searchRows[1] ?? []) }] : [])]
            const gapDays = candidate.second ? Math.round((candidate.second.date.getTime() - candidate.date.getTime()) / 864e5) : 0
            return (
              <div key={candidate.date.getTime()} className="bg-white rounded-md shadow-sm p-3 text-sm">
                <div className="font-bold mb-2">
                  候補{index + 1}：{days.map(v => formatDate(v.date)).join(' → ')}
                  {gapDays > 1 && <span className="ml-2 font-normal text-gray-500">週末・祝日をはさむ</span>}
                  {[...candidate.persons, ...(candidate.second?.persons ?? [])].some(p => p.warnings?.length) && (
                    <span className="ml-2 font-normal text-yellow-700">注意事項があります</span>
                  )}
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="w-28 font-normal"></th>
                      {days.map((v, d) => <th key={d} className="font-normal">{DAY_LABELS[d]} {JSTDate(v.date)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((person, i) => (
                      <tr key={i} className="align-top">
                        <td className="pr-2">{i + 1}人目（{GENDER_LABELS[person.gender]}）</td>
                        {days.map((v, d) => {
                          const schedule = v.persons[i]
                          return (
                            <td key={d} className="pr-3 pb-1">
                              {schedule ? <>
                                <div>受付 {JSTTime(schedule.reception)}・{formatStay(schedule.stayMinutes)}</div>
                                {schedule.warnings?.map(warning => <div key={warning} className="text-yellow-700">注意：{warning}</div>)}
                                <details className="text-gray-500">
                                  <summary className="cursor-pointer">検査ごとの時刻</summary>
                                  {formatScheduledExams(schedule.exams)}
                                </details>
                                <button type="button" className={SMALL_OUTLINE_BUTTON + ' mt-1'}
                                  onClick={() => openSchedule(`候補${index + 1}：${formatDate(v.date)}${candidate.second ? ` ${DAY_LABELS[d]}` : ''}`, v.date, v.persons)}>
                                  スケジュールを開く
                                </button>
                              </> : <span className="text-gray-400">受診なし</span>}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          })}
          <div className="text-xs text-gray-500">
            ※ ドック予約画面の「再計算」と同じ自動スケジュール（検査は{DOCK_EXAM_START}〜{DOCK_EXAM_END}、ES下部は1日{ES_LOWER_DAILY_LIMIT}件まで）で、時刻の重複とPET-CTのIV時刻（決まった時刻のみ・他患者と10分以上）を確認した結果です。検査と検査の間の待ち時間が1回{MAX_WAIT_MINUTES}分を超える日・受付時刻は出しません（受付後の準備20分、PET-CTのIV後の待機、ES下部の前処置2時間は除く）。3階更衣室（同時に8名まで）が満室になる場合は、候補に出したうえで注意として表示します。受付から全検査終了までの時間は、コースごとの上限（各コースの欄に表示）に収まる日だけを出します（「制限時間を外す」にチェックした場合は上限で除外しません。検査は{DOCK_EXAM_END}までに終わる必要があります）。US(L)・子宮細胞診・HPV のどれかがあるコースは、火・水・金の{GYNECOLOGY_START.replace(/^0/, '')}〜{GYNECOLOGY_END}に婦人科の検査が収まる日だけを出します。土日・祝日は対象外です（病院独自の休診日は未対応）。2日連続の場合、2日目は1日目の翌営業日です。
          </div>
        </div>
      ))}

      <div className="bg-white rounded-md shadow-sm p-3 text-sm space-y-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-medium text-base">日付を指定して診断</span>
            <input type="date" className={InputClass + ' h-9 py-1'} value={diagnoseDate} onChange={e => { setDiagnoseDate(e.target.value); setDiagnosis(null) }} />
            <button type="button" className={OUTLINE_BUTTON} onClick={handleDiagnose} disabled={!diagnoseDate || loading}>診断</button>
          </div>
          <p className="text-gray-500 mt-1">
            上の条件で、その日に予約できるかを受付時刻ごとに判定します。{twoDays && '2日連続の場合は、翌営業日の2日目もあわせて判定します。'}
          </p>
        </div>
        {diagnosis && diagnosis.days.map((day, d) => {
          const holiday = getHolidayName(day.date)
          const weekend = day.date.getDay() === 0 || day.date.getDay() === 6
          return (
            <div key={day.date.getTime()} className="border border-gray-200 rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 font-medium flex items-center gap-3 flex-wrap">
                {DAY_LABELS[d]}：{formatDate(day.date)}
                {(holiday || weekend) && (
                  <span className="font-normal text-yellow-700">{holiday ? `祝日（${holiday}）` : '土日'}のため候補日には出ません（再計算での判定は下記）</span>
                )}
              </div>
              {day.attempts.map((attempts, k) => (
                <div key={k} className="px-3 py-2 border-t border-gray-100">
                  <div className="font-medium mb-1">{day.rows[k] + 1}人目（{GENDER_LABELS[people[day.rows[k]]?.gender ?? 'M']}）</div>
                  {attempts.length === 0 ? <div className="text-gray-500">受付候補がありません</div> : (
                    <table className="w-full">
                      <tbody>
                        {attempts.map(attempt => (
                          <tr key={attempt.time} className="align-top">
                            <td className="w-20 py-1">受付 {attempt.time}</td>
                            <td className="w-12 py-1">
                              <span className={`inline-block rounded px-2 text-xs leading-5 ${attempt.reason ? 'bg-red-50 text-red-700' : 'bg-theme-50 text-theme-700'}`}>{attempt.reason ? 'NG' : 'OK'}</span>
                            </td>
                            <td className="py-1">
                              <div className={attempt.reason ? 'text-red-700' : ''}>
                                {attempt.reason ?? formatStay(attempt.stayMinutes ?? 0)}
                              </div>
                              {!attempt.reason && attempt.warnings?.map(warning => <div key={warning} className="text-yellow-700">注意：{warning}</div>)}
                              <div className="text-gray-500 text-xs">{formatScheduledExams(attempt.exams)}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )
        })}
        {diagnosis?.missingSecond && <div className="text-red-600">2日目にできる日（翌営業日）が2週間以内にありません</div>}
      </div>
    </div>
  )
}

export default Candidates
